import React, { useEffect, useState } from "react";
import { TrendingUp, ShieldCheck, HeartPulse, Leaf, Globe, Plus, Pencil, X, FileDown } from "lucide-react";
import { PageHeader, MonthYearFilter, Footer } from "@/components/common";
import ExportDialog from "@/components/ExportDialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { exportToPDF } from "@/lib/pdfExport";
import { MONTHS, MONTH_SHORT } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CATS = {
  safety: { label: "SAFETY (KESELAMATAN)", sub: "Pencegahan Kecelakaan Kerja dan Pencapaian Zero Incident", bg: "bg-blue-700" },
  health: { label: "HEALTH (KESEHATAN)", sub: "Pencegahan Penyakit Akibat Kerja (PAK)", bg: "bg-indigo-700" },
  environment: { label: "ENVIRONMENT (LINGKUNGAN)", sub: "Pencegahan Pencemaran Lingkungan", bg: "bg-green-600" },
};

function pctColor(p) { return p >= 75 ? "#16a34a" : p > 0 ? "#f59e0b" : "#e2e8f0"; }

function KpiCard({ icon: Icon, iconBg, iconColor, label, value, last }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: iconBg }}><Icon className="w-5 h-5" style={{ color: iconColor }} /></div>
        <p className="text-sm text-slate-500 font-medium">{label}</p>
      </div>
      <p className="text-3xl font-extrabold">{value}% <span className="text-xs font-semibold text-green-600">↗ 0%</span></p>
      <p className="text-xs text-slate-400">vs LM: {last}</p>
    </div>
  );
}

const emptyForm = { category: "safety", objective: "", target: "", actual: "", program_kerja: "", frekuensi: "Bulanan", referensi: "", pic: "", keterangan: "", root_cause: "", target_tahunan: 0, months: {} };

function KpiDialog({ open, onOpenChange, initial, year, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  useEffect(() => { setForm(initial ? { ...emptyForm, ...initial } : emptyForm); }, [initial, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMonth = (mm, v) => setForm((f) => ({ ...f, months: { ...f.months, [`${year}-${String(mm).padStart(2, "0")}`]: v === "" ? undefined : Number(v) } }));
  const save = async () => {
    if (!form.objective) { toast.error("Objective wajib diisi"); return; }
    const months = {};
    Object.entries(form.months || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") months[k] = Number(v); });
    const payload = { ...form, target_tahunan: Number(form.target_tahunan) || 0, months };
    try {
      if (initial?.id) await api.put(`/kpi/${initial.id}`, payload);
      else await api.post("/kpi", payload);
      toast.success("Data tersimpan"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error("Gagal menyimpan"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kpi-form">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit" : "Tambah"} KPI Indikator K3</DialogTitle><DialogDescription>Pencapaian & persentase dihitung otomatis dari nilai bulanan.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div><Label className="text-xs">Kategori</Label><Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="kpi-cat"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="safety">Safety</SelectItem><SelectItem value="health">Health</SelectItem><SelectItem value="environment">Environment</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs">Objective (Tujuan)</Label><Input value={form.objective} onChange={(e) => set("objective", e.target.value)} data-testid="kpi-objective" /></div>
          <div><Label className="text-xs">Target (Sasaran)</Label><Input value={form.target} onChange={(e) => set("target", e.target.value)} /></div>
          <div><Label className="text-xs">Actual</Label><Input value={form.actual} onChange={(e) => set("actual", e.target.value)} /></div>
          <div><Label className="text-xs">Program Kerja</Label><Input value={form.program_kerja} onChange={(e) => set("program_kerja", e.target.value)} /></div>
          <div><Label className="text-xs">Frekuensi</Label><Input value={form.frekuensi} onChange={(e) => set("frekuensi", e.target.value)} /></div>
          <div><Label className="text-xs">Referensi</Label><Input value={form.referensi} onChange={(e) => set("referensi", e.target.value)} /></div>
          <div><Label className="text-xs">PIC</Label><Input value={form.pic} onChange={(e) => set("pic", e.target.value)} /></div>
          <div><Label className="text-xs">Keterangan</Label><Input value={form.keterangan} onChange={(e) => set("keterangan", e.target.value)} /></div>
          <div><Label className="text-xs">Root Cause</Label><Input value={form.root_cause} onChange={(e) => set("root_cause", e.target.value)} /></div>
          <div><Label className="text-xs">Target Tahunan</Label><Input type="number" value={form.target_tahunan} onChange={(e) => set("target_tahunan", e.target.value)} data-testid="kpi-target-tahunan" /></div>
        </div>
        <div className="border-t pt-2">
          <p className="text-xs font-semibold text-slate-600 mb-2">Nilai Bulanan {year}</p>
          <div className="grid grid-cols-6 gap-2">
            {MONTH_SHORT.map((m, i) => (
              <div key={m}><Label className="text-[10px]">{m}</Label><Input type="number" className="h-8 text-xs" value={form.months?.[`${year}-${String(i + 1).padStart(2, "0")}`] ?? ""} onChange={(e) => setMonth(i + 1, e.target.value)} /></div>
            ))}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="kpi-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LaggingLeading() {
  const { canEdit } = useAuth();
  const [month, setMonth] = useState(8);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => { const r = await api.get("/kpi", { params: { month, year } }); setData(r.data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year]);

  if (!data) return <div className="p-6"><div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>;

  const s = data.summary;
  const del = async () => { const id = delTarget; setDelTarget(null); try { await api.delete(`/kpi/${id}`); toast.success("KPI dihapus"); load(); } catch (e) { toast.error("Gagal"); } };

  // group rows by category preserving order
  const grouped = [];
  data.rows.forEach((r) => {
    let g = grouped.find((x) => x.cat === r.category);
    if (!g) { g = { cat: r.category, rows: [] }; grouped.push(g); }
    g.rows.push(r);
  });

  const COLS = 8 + 12 + 6;

  return (
    <div className="p-6" id="kpi-page">
      <PageHeader icon={TrendingUp} title="KPI - INDIKATOR KINERJA K3" subtitle={`Tujuan, Sasaran & Program - Data ${MONTHS[month - 1].l} ${year}`}
        right={<div className="flex items-center gap-2">
          {canEdit && <Button size="sm" className="bg-[#0b1220] hover:bg-slate-800 h-9" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="kpi-add"><Plus className="w-4 h-4 mr-1" />Tambah KPI</Button>}
          <Button size="sm" variant="outline" className="h-9" onClick={() => setExportOpen(true)} data-testid="kpi-export"><FileDown className="w-4 h-4 mr-1" />Export PDF</Button>
          <MonthYearFilter month={month} setMonth={setMonth} year={year} setYear={setYear} />
        </div>} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={ShieldCheck} iconBg="#fee2e2" iconColor="#dc2626" label="Total Safety Pencapaian %" value={s.safety} last={s.safety} />
        <KpiCard icon={HeartPulse} iconBg="#fce7f3" iconColor="#db2777" label="Total Health Pencapaian %" value={s.health} last={s.health} />
        <KpiCard icon={Leaf} iconBg="#dcfce7" iconColor="#16a34a" label="Total Environment Pencapaian %" value={s.environment} last={s.environment} />
        <KpiCard icon={Globe} iconBg="#dbeafe" iconColor="#2563eb" label="Global % Pencapaian" value={s.global} last={s.global} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-xs w-full" data-testid="kpi-table">
            <thead>
              <tr className="bg-slate-100 text-slate-600 border-b">
                <th rowSpan={2} className="px-2 py-2">No</th>
                <th rowSpan={2} className="px-2 py-2 text-left min-w-[130px]">Objective (Tujuan)</th>
                <th rowSpan={2} className="px-2 py-2">Target (Sasaran)</th>
                <th rowSpan={2} className="px-2 py-2">Actual</th>
                <th rowSpan={2} className="px-2 py-2 min-w-[110px]">Program Kerja</th>
                <th rowSpan={2} className="px-2 py-2">Frekuensi</th>
                <th rowSpan={2} className="px-2 py-2">Referensi</th>
                <th rowSpan={2} className="px-2 py-2">PIC</th>
                <th colSpan={12} className="px-2 py-1 text-center border-l border-slate-200">Bulan</th>
                <th rowSpan={2} className="px-2 py-2 min-w-[100px] border-l">Keterangan</th>
                <th rowSpan={2} className="px-2 py-2">Target Tahunan</th>
                <th rowSpan={2} className="px-2 py-2">Pencapaian</th>
                <th rowSpan={2} className="px-2 py-2 min-w-[120px]">Persentase (%)</th>
                <th rowSpan={2} className="px-2 py-2">Root Cause</th>
                {canEdit && <th rowSpan={2} className="px-2 py-2">Aksi</th>}
              </tr>
              <tr className="bg-slate-100 text-slate-500 border-b">
                {MONTH_SHORT.map((m, i) => <th key={m} className={`px-1 py-1 font-medium ${i + 1 > month ? "text-slate-300" : ""} ${i === 0 ? "border-l border-slate-200" : ""}`}>{m}</th>)}
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <React.Fragment key={g.cat}>
                  <tr><td colSpan={COLS} className={`${CATS[g.cat].bg} text-white px-3 py-2 font-bold`}>{CATS[g.cat].label} | <span className="font-medium italic">{CATS[g.cat].sub}</span></td></tr>
                  {g.rows.map((r, idx) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-2 text-center">{idx + 1}</td>
                      <td className="px-2 py-2 font-medium">{r.objective}</td>
                      <td className="px-2 py-2 text-center">{r.target}</td>
                      <td className="px-2 py-2 text-center">{r.actual}</td>
                      <td className="px-2 py-2">{r.program_kerja}</td>
                      <td className="px-2 py-2 text-center">{r.frekuensi}</td>
                      <td className="px-2 py-2">{r.referensi}</td>
                      <td className="px-2 py-2">{r.pic}</td>
                      {r.monthly.map((v, i) => <td key={i} className={`px-1 py-2 text-center ${i === 0 ? "border-l border-slate-100" : ""} ${i + 1 > month ? "bg-slate-50" : ""}`}>{v ?? ""}</td>)}
                      <td className="px-2 py-2 border-l">{r.keterangan}</td>
                      <td className="px-2 py-2 text-center font-semibold">{r.target_tahunan}</td>
                      <td className="px-2 py-2 text-center font-semibold">{r.pencapaian}</td>
                      <td className="px-2 py-2"><div className="flex items-center gap-2"><div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden"><div className="h-3 rounded-full" style={{ width: `${Math.min(r.persentase, 100)}%`, background: pctColor(r.persentase) }} /></div><span className="text-[10px] font-bold w-9 text-right">{r.persentase}%</span></div></td>
                      <td className="px-2 py-2">{r.root_cause}</td>
                      {canEdit && <td className="px-2 py-2 whitespace-nowrap"><div className="flex items-center gap-1">
                        <button onClick={() => { setEditing(r); setFormOpen(true); }} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" data-testid={`kpi-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setDelTarget(r.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`kpi-del-${r.id}`}><X className="w-3.5 h-3.5" /></button></div></td>}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <KpiDialog open={formOpen} onOpenChange={setFormOpen} initial={editing} year={year} onSaved={load} />
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Hapus KPI?</AlertDialogTitle><AlertDialogDescription>Baris indikator ini akan dihapus permanen.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={del} data-testid="kpi-del-confirm">Hapus</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} onExport={(o) => exportToPDF("kpi-page", `KPI_K3_${month}_${year}`, o)} />
      <Footer />
    </div>
  );
}
